// Algolia limits the amount of nested AND/OR clauses that can be used in
// a single query so in order to get all products for a category or
// featured section (e.g. Twic Picks) across all wallets we have to use
// algoliaClient.multipleQueries() which results in the possibility
// of returning duplicates if multiple wallets are eligible for
// the same product (i.e. common categories/subcategories between wallets).
// To prevent the user from seeing the duplicates we filter them out before
// rendering the UI in common/ProductList. This works well enough, but will
// become somewhat problematic when showing search results where the total
// product count is shown before fetching all of the products themselves.
// We'll probably need to address this at some point in the future.

import type { SearchResponse } from '@algolia/client-search';

import type { IEmployeeWallet } from 'hooks/useQueryProfile';

// This is incomplete
export interface Product {
  id: string;
  product_id: string;
  vendor_id: string;
  categories: string[];
  coupon_code_available: boolean;
  coupon_code_locations: string[];
  product_instruction_image: string[];
  product_image_url: {
    image_url: string;
  };
  product_title: string;
  subcategories: string[];
  vendor_title: string;
  term: string;
  usd_special_price: number;
  usd_msrp: number;
}

export const not = (value: string) => `-${value}`;

export const getProductsQueryParams = ({
  employee_wallet,
  country,
  subcategories,
  facet_filters = [],
  numeric_filters = [],
}: {
  employee_wallet: IEmployeeWallet;
  country: string;
  subcategories?: string[];
  facet_filters?: readonly string[] | ReadonlyArray<readonly string[] | string>;
  numeric_filters?: readonly string[] | ReadonlyArray<readonly string[]>;
}) => {
  const vendor_facet_filters =
    employee_wallet.company_wallet_configuration.blacklisted_vendors.map(
      (vid: string) => `vendor_id:${not(vid)}`
    );
  const subcats =
    subcategories ??
    employee_wallet.company_wallet_configuration.categories.flatMap(
      ({ subcategories }) =>
        subcategories.map(({ value }) => value).filter((value) => value)
    );
  const subcategory_facet_filters = !subcats.length
    ? ['subcategories:no_categories']
    : subcats.map((subcat: string) => `subcategories:${subcat}`);

  const base_facet_filters = [
    'is_product_active:true',
    'is_vendor_active:true',
    `available_countries:${country}`,
    ...vendor_facet_filters,
    subcategory_facet_filters,
  ];

  return {
    facet_filters: [...base_facet_filters, ...facet_filters],
    numeric_filters,
  };
};

export const groupAndCountResultsForMultipleWallets = <T = unknown>(
  results: Array<SearchResponse<T>>
) =>
  results.reduce<{ items: Array<T>; count: number; pages: number }>(
    (acc, result) => {
      acc.items.push(...result.hits);
      acc.count += result.nbHits;
      acc.pages = Math.max(acc.pages, result.nbPages);
      return acc;
    },
    { items: [], count: 0, pages: 0 }
  );
